import { TableCard, TableOutput } from "@general-backoffice/core";
import type { PaginationMeta, Referral } from "@investhinks/api-client";
import moment from "moment";
import React from "react";
import { Button, ButtonGroup, Card } from "reactstrap";

import { TypedTable, type TypedTableColumn } from "../../utils/typedLib";
import PaginatedTableFooter from "../shared/PaginatedTableFooter";

interface ReferralFilters {
  expired?: boolean | null;
  claimed?: boolean;
  [key: string]: unknown;
}

interface AdminReferralsTableCardProps {
  referrals: Referral[];
  showCommissions?: boolean;
  totalReferrals: number;
  isLoading: boolean;
  pagination: PaginationMeta;
  goToPage: (page: number) => void;
  filters: ReferralFilters;
  resetFilters: () => void;
  resetPagination: () => void;
  setFilters: (filters: ReferralFilters | Record<string, unknown>) => void;
  [key: string]: unknown;
}

const AdminReferralsTableCard = ({
  referrals,
  showCommissions = true,
  totalReferrals,
  isLoading,
  pagination,
  goToPage,
  filters,
  resetFilters,
  resetPagination,
  setFilters,
}: AdminReferralsTableCardProps) => {
  const columns: TypedTableColumn<Referral>[] = [
    {
      name: "User",
      compressed: true,
      formattedContent: ({ referee }) => (
        <>
          <div>{referee?.name}</div>
          <small className="font-weight-bold">{referee?.email}</small>
        </>
      ),
      isTh: true,
    },
    {
      name: "",
      formattedContent: ({ referee }) => {
        const bookmarks = [];
        if (!referee?.isActive)
          bookmarks.push({
            icon: <i className="fas fa-exclamation-triangle" />,
          });
        return <TableOutput.Bookmarks bookmarks={bookmarks} />;
      },
    },
    {
      name: "Link Invitation",
      formattedContent: ({ referral_code }) => referral_code?.name || "Link Invitation",
    },
    {
      name: "Com. to Claim",
      hidden: !showCommissions,
      isTh: true,
      formattedContent: ({ uncollectedCommission }) => (uncollectedCommission || 0).toFixed(2) + " €",
    },
    {
      name: "Total Generated Com.",
      hidden: !showCommissions,
      formattedContent: ({ totalCommission }) => (totalCommission || 0).toFixed(2) + " €",
    },
    {
      name: "Registration Date",
      formattedContent: ({ createdAt }) => moment(createdAt).format("LL"),
    },
  ];

  const buttons = [
    {
      children: "Active",
      active: !filters?.expired && !filters?.claimed,
      onClick: () => resetFilters(),
    },
    {
      children: "Claimed",
      active: !!filters?.claimed,
      onClick: () =>
        setFilters({
          expired: null,
          claimed: true,
        }),
    },
    {
      children: "Expired",
      active: !!filters?.expired && !filters?.claimed,
      onClick: () =>
        setFilters({
          expired: true,
          claimed: false,
        }),
    },
  ];

  return (
    <Card className="overflow-hidden">
      <TableCard.Header title={"Referrals"} subtitle={`Referral's total: ${totalReferrals}`} isLoading={isLoading}>
        <ButtonGroup>
          {buttons.map((b, index) => (
            <Button
              {...b}
              size={"sm"}
              onClick={() => {
                resetPagination();
                b.onClick();
              }}
              key={index}
              color="default"
              outline
            />
          ))}
        </ButtonGroup>
      </TableCard.Header>
      <TypedTable
        columns={columns}
        rows={referrals}
        isLoading={isLoading}
        noContentMessage={"Empty referrals"}
        rowAmount={pagination.pageSize}
        selectable={false}
        sortable={false}
      />
      <PaginatedTableFooter
        {...{
          isLoading,
          pagination,
          goToPage,
        }}
      />
    </Card>
  );
};

export default AdminReferralsTableCard;
